/**
 * Similar Transactions API Service
 * Handles finding similar uncategorized transactions for bulk categorization
 * Based on WP-CAT-2: Apply to Similar Transactions Feature
 */
import api from './api';
import type { components } from '@/types/api.generated';

/** Wire types for PATCH /v1/transactions/bulk, generated from core's OpenAPI spec. */
type ApiBulkUpdateRequest = components['schemas']['BulkUpdateRequest'];
type ApiBulkUpdateResponse = components['schemas']['BulkUpdateResponse'];

/**
 * Most transaction ids one PATCH /v1/transactions/bulk request may carry.
 *
 * Mirrors `BULK_UPDATE_MAX_TRANSACTIONS` in services/core/app/schemas/transaction.py
 * (published as `maxItems` on BulkUpdateRequest.transactionIds in
 * docs/api/core-openapi.json). The generated types do not carry the number, so
 * it is defined once here; change both together. Larger selections are sent in
 * chunks of this size (WP-BUG-65).
 */
export const BULK_UPDATE_MAX_TRANSACTIONS = 1000;

// ============================================================================
// Types
// ============================================================================

/**
 * A similar transaction item returned from the API
 */
export interface SimilarTransactionItem {
  id: string;
  name: string;
  merchantName: string | null;
  amount: number;
  date: string;
  accountName: string;
}

/**
 * Response from GET /transactions/{id}/similar
 */
export interface SimilarTransactionsResponse {
  originalTransaction: SimilarTransactionItem;
  similarTransactions: SimilarTransactionItem[];
  /**
   * Rule pattern to offer, or null when there is none: the server sends null for a
   * person-to-person payment (WP-SEC-20) and "" when it extracted nothing; both are null here.
   */
  suggestedPattern: string | null;
  totalCount: number;
}

/**
 * Request for bulk update with optional rule creation
 */
export interface BulkUpdateWithRuleRequest {
  transactionIds: string[];
  categoryId: string;
  createRule?: boolean;
  rulePattern?: string;
  ruleName?: string;
}

/**
 * Response from bulk update with rule creation (BulkUpdateResponse)
 */
export interface BulkUpdateWithRuleResponse {
  updatedCount: number;
  ruleCreated: boolean;
  ruleId?: string;
  rulePattern?: string;
}

/**
 * Some chunks of a chunked bulk update were applied and a later one failed.
 *
 * Chunks are sent in order and sending stops at the first failure, so
 * `updatedCount` reflects the chunks that succeeded and `failedCount` is every
 * selected id that was not sent successfully. Re-applying the same category to
 * the whole selection is safe: it is idempotent.
 */
export class BulkUpdatePartialError extends Error {
  readonly updatedCount: number;
  readonly failedCount: number;
  readonly totalCount: number;
  readonly ruleCreated: boolean;
  readonly cause: unknown;

  constructor(params: {
    updatedCount: number;
    failedCount: number;
    totalCount: number;
    ruleCreated: boolean;
    cause: unknown;
  }) {
    super(
      `Updated ${params.updatedCount} transactions, but ${params.failedCount} of ` +
        `${params.totalCount} selected could not be updated. Please try again.`
    );
    this.name = 'BulkUpdatePartialError';
    this.updatedCount = params.updatedCount;
    this.failedCount = params.failedCount;
    this.totalCount = params.totalCount;
    this.ruleCreated = params.ruleCreated;
    this.cause = params.cause;
  }
}

/** Split ids into consecutive chunks of at most `size`. */
export function chunkIds(ids: string[], size: number = BULK_UPDATE_MAX_TRANSACTIONS): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

// ============================================================================
// Similar Transactions Service
// ============================================================================

export const similarTransactionsService = {
  /**
   * Get similar uncategorized transactions for a given transaction
   * @param transactionId - The ID of the transaction to find similar transactions for
   * @param limit - Maximum number of similar transactions to return (default: 50)
   */
  getSimilarTransactions: async (
    transactionId: string,
    limit: number = 50
  ): Promise<SimilarTransactionsResponse> => {
    const queryParams: Record<string, unknown> = {
      limit,
    };

    // Backend returns snake_case, transform to camelCase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(
      `/transactions/${transactionId}/similar`,
      queryParams
    );

    // Transform from API response to frontend types
    // Handle both camelCase (actual API) and snake_case (legacy) field names
    return {
      originalTransaction: transformSimilarTransactionItem(response.originalTransaction ?? response.original_transaction),
      similarTransactions: (response.similarTransactions ?? response.similar_transactions ?? []).map(transformSimilarTransactionItem),
      suggestedPattern: (response.suggestedPattern ?? response.suggested_pattern) || null,
      totalCount: response.totalCount ?? response.total_count ?? 0,
    };
  },

  /**
   * Bulk update transactions with optional rule creation
   * PATCH /v1/transactions/bulk (api-surface.md section 9, WP-BUG-65).
   * The fields to change go under `payload`; the body is camelCase.
   *
   * Selections larger than BULK_UPDATE_MAX_TRANSACTIONS are sent as sequential
   * chunks. Only the first chunk asks for a rule, so a rule is created at most
   * once. If the first chunk fails its error is rethrown unchanged (nothing was
   * applied); if a later chunk fails, BulkUpdatePartialError reports how many
   * were updated and how many were not.
   */
  bulkUpdateWithRule: async (
    request: BulkUpdateWithRuleRequest
  ): Promise<BulkUpdateWithRuleResponse> => {
    const chunks = chunkIds(request.transactionIds);
    const result: BulkUpdateWithRuleResponse = { updatedCount: 0, ruleCreated: false };
    let sent = 0;

    for (const [index, transactionIds] of chunks.entries()) {
      const isFirst = index === 0;
      const body: ApiBulkUpdateRequest = {
        transactionIds,
        payload: { categoryId: request.categoryId },
        createRule: isFirst ? (request.createRule ?? false) : false,
        rulePattern: isFirst ? request.rulePattern : undefined,
        ruleName: isFirst ? request.ruleName : undefined,
      };

      let response: ApiBulkUpdateResponse;
      try {
        response = await api.patch<ApiBulkUpdateResponse>('/transactions/bulk', body);
      } catch (error) {
        if (isFirst) {
          throw error;
        }
        throw new BulkUpdatePartialError({
          updatedCount: result.updatedCount,
          failedCount: request.transactionIds.length - sent,
          totalCount: request.transactionIds.length,
          ruleCreated: result.ruleCreated,
          cause: error,
        });
      }

      sent += transactionIds.length;
      result.updatedCount += response.updatedCount;
      if (isFirst) {
        result.ruleCreated = response.ruleCreated;
        result.ruleId = response.ruleId ?? undefined;
        result.rulePattern = response.rulePattern ?? undefined;
      }
    }

    return result;
  },
};

/**
 * Transform a single similar transaction item from snake_case to camelCase
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformSimilarTransactionItem(item: any): SimilarTransactionItem {
  if (!item) {
    return {
      id: '',
      name: 'Unknown',
      merchantName: null,
      amount: 0,
      date: '',
      accountName: '',
    };
  }

  return {
    id: item.id ?? '',
    name: item.name ?? 'Unknown',
    merchantName: item.merchant_name ?? item.merchantName ?? null,
    amount: Number(item.amount) || 0,
    date: item.date ?? '',
    accountName: item.account_name ?? item.accountName ?? '',
  };
}

// Named export for consistency with other services
export const getSimilarTransactions = similarTransactionsService.getSimilarTransactions;
export const bulkUpdateWithRule = similarTransactionsService.bulkUpdateWithRule;

export default similarTransactionsService;
